import { z } from 'zod';
import { cellRowTypeSchema } from './cell.js';
import { palierNumberSchema } from './palier.js';

export const matrixGenerationJobStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
]);
export type MatrixGenerationJobStatus = z.infer<typeof matrixGenerationJobStatusSchema>;

/**
 * What a job is for (2026-08-26 design doc, D15). `digest_only` stops after the
 * synthesis; `full` continues into cells.
 */
export const matrixGenerationJobModeSchema = z.enum(['digest_only', 'full']);
export type MatrixGenerationJobMode = z.infer<typeof matrixGenerationJobModeSchema>;

/**
 * Which phase a running job is in (D14). Null while pending — and on jobs that
 * predate phases entirely.
 */
export const matrixGenerationJobPhaseSchema = z.enum(['digest', 'synthesis', 'cells']);
export type MatrixGenerationJobPhase = z.infer<typeof matrixGenerationJobPhaseSchema>;

export const matrixGenerationJobSchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  status: matrixGenerationJobStatusSchema,
  mode: matrixGenerationJobModeSchema,
  phase: matrixGenerationJobPhaseSchema.nullable(),
  columnNumbers: z.array(palierNumberSchema),
  error: z.string().nullable(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
});
export type MatrixGenerationJob = z.infer<typeof matrixGenerationJobSchema>;

/**
 * One past generation of a matrix, as the Cadrage tab's Prompt section reads
 * it (issue #3) — `GET /matrices/:id/generation-prompts`, pms_admin-only.
 *
 * `prompt` is the **exact** text that was handed to the model when the job
 * started: runner prompt (variables filled) + composed context + documents +
 * output instructions, concatenated. That distinction is the whole point of
 * persisting it. `GET /matrices/:id/composed-prompt` recomposes from the
 * matrix as it stands *now* and returns only the runner prompt and the
 * context; showing that and calling it "the prompt that was used" would be
 * wrong the first time anyone edits the sujet, and wrong about the documents
 * and the JSON contract even before that.
 *
 * `prompt` is null for every job that ran before those columns existed. Such a
 * job is not a broken record and must not be presented as one — the frontend
 * falls back to the live recomposition behind an explicit "reconstitué"
 * warning, never verbatim.
 *
 * Only `mode: 'full'` jobs appear here. A `digest_only` job never composes a
 * cell prompt, and regeneration prompts live in `regeneration_batches`, which
 * this pass deliberately leaves unrecorded.
 */
export const matrixGenerationPromptSchema = z.object({
  jobId: z.string().uuid(),
  status: matrixGenerationJobStatusSchema,
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  /** Null on jobs older than the columns — see the note above. */
  prompt: z.string().nullable(),
  /** The Langfuse prompt this run resolved, e.g. `mss-creator-runner`. Null alongside `prompt`. */
  runnerPromptName: z.string().nullable(),
  runnerPromptVersion: z.number().int().nullable(),
  /**
   * True when the stored prompt embeds the matrix's `initialPromptOverride`
   * verbatim — i.e. a pms_admin hand-edited the composed prompt in the
   * récapitulatif before launching this run.
   *
   * Derived rather than stored: the override itself is a mutable field on the
   * matrix, and containment is exact both ways. A run that used today's
   * override contains it; one launched under an older (or no) override does
   * not, and correctly goes unbadged. Always false when `prompt` is null —
   * a reconstituted prompt says nothing about what was sent.
   */
  usedPromptOverride: z.boolean(),
});
export type MatrixGenerationPrompt = z.infer<typeof matrixGenerationPromptSchema>;

/** `POST /matrices/:id/generation-jobs` — omitted/absent `columnNumbers` means "all 8 paliers". */
export const createMatrixGenerationJobInputSchema = z.object({
  columnNumbers: z.array(palierNumberSchema).min(1).optional(),
});
export type CreateMatrixGenerationJobInput = z.infer<typeof createMatrixGenerationJobInputSchema>;

/** One entry of `claude`'s required structured-JSON response. */
export const generatedCellSchema = z.object({
  rowType: cellRowTypeSchema,
  rowRef: z.string().min(1),
  columnNumber: palierNumberSchema,
  content: z.string().min(1),
});
export type GeneratedCell = z.infer<typeof generatedCellSchema>;

export const generatedCellsPayloadSchema = z.object({
  cells: z.array(generatedCellSchema).min(1),
});
export type GeneratedCellsPayload = z.infer<typeof generatedCellsPayloadSchema>;
